import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";

import { EntityPin, MultiPin } from "@mapmycustomers/shared/types/map";
import {
  convertLatLngBoundsToPlatformBounds,
  convertLatLngToLongLat,
  convertLongLatToLatLngLiteral,
} from "@mapmycustomers/shared/util/geo/GeoService";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";

import EntityAnnotation, { EntityAnnotationProps } from "@app/component/EntityAnnotation";
import {
  FindMeButton,
  useMap,
  useMapEventHandler,
  UserLocation,
  ZoomButtons,
} from "@app/component/map/FeaturedMap";
import ClusterFeature from "@app/component/map/FeaturedMap/components/ClusterFeature";
import EntityFeature from "@app/component/map/FeaturedMap/components/EntityFeature";
import MultiPinFeature from "@app/component/map/FeaturedMap/components/MultiPinFeature";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import MultiPinRecordListDrawer from "@app/component/map/MultiPinRecordListDrawer";
import { showMultiPinRecords } from "@app/component/map/MultiPinRecordListDrawer/store/actions";
import NoContactInCardData from "@app/scene/dashboard/store/cards/noContactIn/NoContactInCardData";
import { showEntityView } from "@app/store/entityView/actions";
import { openOnMapPage } from "@app/store/map/actions";
import NoContactInCardConfiguration from "@app/types/dashboard/cards/noContactIn/NoContactInCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import MapViewportState from "@app/types/map/MapViewportState";

import styles from "./MapContent.module.scss";
import OpenOnMap from "./OpenOnMap";
import createMapViewStateFromConfiguration from "./utils/createMapViewStateFromConfiguration";

// wait for max 500ms before triggering next onViewportChange callback
const boundsChangeOptions = { maxWait: 500 };

interface OwnProps {
  configuration: NoContactInCardConfiguration;
  // configuration key is used to re-initialize (set initial bounds to fit
  // in all points) map every time configuration changes.
  // User can pan/zoom map after that without any re-initializations.
  configurationKey: number;
  data?: NoContactInCardData;
  scope: Scope;
}

interface Props extends OwnProps {
  onOpenOnMap: typeof openOnMapPage;
  onShowMultiPinRecords: typeof showMultiPinRecords.request;
  onShowPreviewPane: typeof showEntityView;
  onViewportChange?: (viewport: MapViewportState) => void;
}

const MapContent: React.FC<Props> = ({
  configuration,
  configurationKey,
  data,
  onOpenOnMap,
  onShowMultiPinRecords,
  onShowPreviewPane,
  onViewportChange,
  scope,
}) => {
  const { map } = useMap();

  const previousConfigurationKey = useRef<number>();
  useEffect(() => {
    if (!data?.categorizedMapEntries || previousConfigurationKey.current === configurationKey) {
      return;
    }
    previousConfigurationKey.current = configurationKey;

    const bounds = new google.maps.LatLngBounds();
    Object.values(data?.categorizedMapEntries ?? {})
      .flat()
      .forEach((mapEntry) => {
        bounds.extend(convertLongLatToLatLngLiteral(mapEntry.bounds.top_left));
        bounds.extend(convertLongLatToLatLngLiteral(mapEntry.bounds.bottom_right));
      });
    map.fitBounds(bounds);
  }, [configurationKey, data, map]);

  const [annotation, setAnnotation] = useState<
    Pick<EntityAnnotationProps, "entityId" | "entityType"> | undefined
  >();
  const handlePinClick = useCallback((entity: EntityPin) => {
    setAnnotation({ entityId: entity.data.id, entityType: entity.data.entity });
  }, []);
  const handleHideAnnotation = useCallback(() => setAnnotation(undefined), []);

  const handlePinDoubleClick = useCallback(
    (entity: EntityPin) => {
      onShowPreviewPane({ entityId: entity.data.id, entityType: entity.data.entity });
    },
    [onShowPreviewPane]
  );

  const handleShowMultiPinRecords = useCallback(
    (multiPin: MultiPin) => {
      onShowMultiPinRecords({ multiPin, visibleEntities: configuration.criteria.entityTypes });
    },
    [configuration, onShowMultiPinRecords]
  );

  useMapEventHandler(
    // We don't use `idle` because it is not triggered if you stopped moving but keep mouse
    // button pressed. Hence, we use debounce to avoid calling backend too frequently.
    // However, we still use maxWait to guarantee that at least one request is sent every
    // maxWait milliseconds. This is needed to load data if user is panning continuously.
    "bounds_changed",
    useDebouncedCallback([
      function (this: google.maps.Map) {
        const bounds = this.getBounds();
        const zoom = this.getZoom();
        if (bounds && zoom) {
          onViewportChange?.({ bounds: convertLatLngBoundsToPlatformBounds(bounds), zoom });
        }
      },
      100,
      boundsChangeOptions,
    ])
  );

  const handleOpenOnMap = useCallback(() => {
    const mapCenter = map.getCenter();

    onOpenOnMap({
      mapViewState: createMapViewStateFromConfiguration(configuration, scope),
      openOnNewTab: true,
      viewport: {
        center: mapCenter ? convertLatLngToLongLat(mapCenter) : undefined,
        zoom: map.getZoom(),
      },
    });
  }, [configuration, map, onOpenOnMap, scope]);

  if (!data?.categorizedMapEntries) {
    return null;
  }

  return (
    <div className={styles.container}>
      <EntityFeature
        entityPins={data.categorizedMapEntries.entities}
        onClick={handlePinClick}
        onDoubleClick={handlePinDoubleClick}
      />
      <MultiPinFeature
        multiPins={data.categorizedMapEntries.multiPins}
        onShowMultiPinRecords={handleShowMultiPinRecords}
      />
      <ClusterFeature clusters={data.categorizedMapEntries.clusters} />
      <UserLocation />
      <Interactive className={styles.findMeButton}>
        <FindMeButton />
      </Interactive>
      <Interactive className={styles.zoomButtons}>
        <ZoomButtons />
      </Interactive>
      <Interactive className={styles.openOnMap}>
        <OpenOnMap onClick={handleOpenOnMap} />
      </Interactive>
      {annotation && (
        <EntityAnnotation
          entityId={annotation.entityId}
          entityType={annotation.entityType}
          map={map}
          onHide={handleHideAnnotation}
        />
      )}
      <MultiPinRecordListDrawer />
    </div>
  );
};

const mapDispatchToProps = {
  onOpenOnMap: openOnMapPage,
  onShowMultiPinRecords: showMultiPinRecords.request,
  onShowPreviewPane: showEntityView,
};

export default connect(null, mapDispatchToProps)(MapContent);
